'use client'

import { Dispatch, SetStateAction } from 'react'
import { Minus, Plus, Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface QuantityInputProps {
  value: number
  onChange?: Dispatch<SetStateAction<number>>
  min?: number
  max?: number
  className?: string
  iconSize?: number
  loading?: boolean
}

const QuantityInput = ({
  value,
  onChange,
  min = 1,
  max = 999,
  className,
  iconSize = 24,
  loading = false,
}: QuantityInputProps) => {
  const isMin = (number: number = value) => number <= min
  const isMax = (number: number = value) => number >= max

  const handleTotalCount = (
    isUp: boolean,
    isMaxOrMin: (number?: number) => boolean,
  ) => {
    const isNotValid = isMaxOrMin()

    if (isNotValid) return
    onChange?.((old) => (isUp ? old + 1 : old - 1))
  }

  const handleOnClick = {
    up: () => {
      handleTotalCount(true, isMax)
    },
    down: () => {
      handleTotalCount(false, isMin)
    },
  }

  const handleOnChange = (realValue: number) => {
    const value = isMin(realValue) ? min : isMax(realValue) ? max : realValue
    onChange?.(value)
  }

  return (
    <div
      className={cn(
        'relative flex w-[112px] items-center border-[0.3px] border-black bg-white px-2 text-xl',
        className,
      )}
    >
      <button
        onClick={() => handleOnClick.down()}
        className={cn(
          'flex h-full w-6 items-center justify-center',
          (isMin() || loading) && 'text-grey-600',
        )}
        disabled={loading || isMin()}
      >
        <Minus strokeWidth={1} size={iconSize} />
      </button>

      <Input
        type="number"
        value={value}
        onChange={(e) => handleOnChange(+e.currentTarget.value)}
        className={cn(
          'h-full flex-1 rounded-none border-0 bg-white p-0 text-center leading-5.5',
          '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        )}
        style={{ fontSize: 'inherit' }}
        disabled={loading}
      />

      <button
        onClick={() => handleOnClick.up()}
        className={cn(
          'flex h-full w-6 items-center justify-center',
          (isMax() || loading) && 'text-grey-600',
        )}
        disabled={loading || isMax()}
      >
        <Plus strokeWidth={1} size={iconSize} />
      </button>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50">
          <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
        </div>
      )}
    </div>
  )
}

export default QuantityInput
